import { defineStore } from 'pinia';
import { ref } from 'vue';
import { directoryService, normalizePersonName } from '@/services/erpat';
import { IS_DEV } from '@/utils/env';

/** How many per-user profile lookups may be in flight at once. */
const CONCURRENCY = 6;

/**
 * Only a positive integer can be a `users.id`: the backend route is
 * `users/{id}/profile` with `{id}` limited to `[0-9]+`. Anything else is dropped
 * before it costs a request: the `ME` sentinel ('me'), a stray route param
 * (`/profile/me`), or a `"0"` from a NULL/legacy column (always a 404). A
 * non-numeric id gets CodeIgniter's HTML 404, which also carries no CORS headers,
 * so a cross-origin build sees a network error rather than a readable miss.
 */
const USER_ID_RE = /^[1-9]\d*$/;

/**
 * A person as a screen knows them, for {@link photoFor} / {@link ensureFor}.
 * `separated` = they have LEFT the company (the server's `*_separated` flag): the
 * payload already carries the "Resigned Employee #<job id>" label and no photo.
 */
export interface AvatarSubject {
  id?: string;
  name?: string;
  image?: string;
  separated?: boolean;
}

/**
 * Shared `userId -> avatar URL` directory for every screen that lists people —
 * the team-attendance rows, the compose contacts picker, the department roster.
 * None of those payloads reliably carries a photo, so without this they all show
 * initials.
 *
 * Two sources, cheapest first:
 *  1. {@link directoryService.avatars} — one bulk pass over the caller's teams.
 *  2. {@link directoryService.userAvatar} — `/v1/api/users/{id}/profile` per
 *     user, for anyone the bulk pass didn't resolve. Scoped to the caller's
 *     messaging contacts, so some ids legitimately resolve to nothing.
 *
 * Every failure is swallowed: no avatar just means initials, exactly as before.
 * Each id is attempted at most ONCE per session (misses included), so a 403 is
 * never re-requested.
 */
export const useAvatarsStore = defineStore('avatars', () => {
  const byUserId = ref<Record<string, string>>({});
  const loaded = ref(false);

  /** Ids already looked up, hit or miss — negative caching. */
  const attempted = ref(new Set<string>());

  // Single-flight for the bulk pass: several screens can call ensureLoaded() on
  // the same tick and it costs one request per team.
  let inFlight: Promise<void> | null = null;

  /**
   * Absolute avatar URL for a person, or '' when unknown.
   *
   * `name` is a second chance for rows whose id is not a USER id — the team
   * attendance surface nests its own `employee` record, whose id may belong to
   * the HR employee table rather than the user table, and the name is the only
   * field both sides always carry.
   */
  function get(userId: string | undefined, name?: string): string {
    if (userId) {
      const byId = byUserId.value[userId];
      if (byId) return byId;
    }
    if (name) return byUserId.value[`name:${normalizePersonName(name)}`] ?? '';
    return '';
  }

  /** Bulk pass over the caller's teams. Runs at most once per session. */
  async function ensureLoaded(): Promise<void> {
    if (loaded.value) return;
    if (inFlight) return inFlight;

    inFlight = directoryService
      .avatars()
      .then((directory) => {
        byUserId.value = { ...byUserId.value, ...directory };
        for (const id of Object.keys(directory)) attempted.value.add(id);
        loaded.value = true;
      })
      .catch(() => {
        // Leave `loaded` false so a later screen can retry the bulk pass.
      })
      .finally(() => {
        inFlight = null;
      });

    return inFlight;
  }

  // One shared queue with a bounded pool, so a screen full of avatars each
  // asking for a single id still issues a handful of requests at a time rather
  // than one per avatar all at once.
  const queue: string[] = [];
  let draining = false;

  async function drain(): Promise<void> {
    if (draining) return;
    draining = true;

    // Dev-only tally so a run that resolves nothing says WHY — an empty `image`
    // in the payload, a 403 (outside the caller's messaging scope), or a 404
    // (the id is not a user id) are three different problems with three
    // different fixes, and they are indistinguishable from "still initials".
    const report: Array<{ userId: string; result: string }> = [];

    async function worker(): Promise<void> {
      for (let id = queue.shift(); id; id = queue.shift()) {
        try {
          const image = await directoryService.userAvatar(id);
          // Reassign per hit so avatars fill in progressively, not all at once.
          if (image) byUserId.value = { ...byUserId.value, [id]: image };
          if (IS_DEV) report.push({ userId: id, result: image || 'empty image field' });
        } catch (err) {
          // Out of messaging scope / no such user — stays a miss.
          if (IS_DEV) {
            const status = (err as { status?: number }).status;
            report.push({ userId: id, result: status ? `HTTP ${status}` : 'request failed' });
          }
        }
      }
    }

    await Promise.all(Array.from({ length: CONCURRENCY }, worker));

    if (IS_DEV && report.length) {
      const hits = report.filter((r) => r.result.startsWith('http')).length;
      // eslint-disable-next-line no-console
      console.info(
        `[avatars] resolved ${hits}/${report.length} via /v1/api/users/{id}/profile`,
      );
      // eslint-disable-next-line no-console
      if (hits < report.length) console.table(report);
    }
    draining = false;
    // Ids enqueued while the pool was winding down.
    if (queue.length) void drain();
  }

  /**
   * Resolve avatars for specific users, one profile request each. `limit` caps a
   * single call so a long list (the contacts picker) can't fan out into hundreds
   * of requests — pass the ids actually on screen and call again as the list
   * changes.
   */
  function ensureUsers(ids: Array<string | undefined>, limit = 24): void {
    let queued = 0;
    for (const id of ids) {
      if (!id || !USER_ID_RE.test(id) || attempted.value.has(id)) continue;
      // Mark up front so a re-render mid-flight can't queue the same id twice.
      attempted.value.add(id);
      queue.push(id);
      if (++queued >= limit) break;
    }
    if (queue.length) void drain();
  }

  /**
   * The photo to show for a person: the payload's own, else this directory's.
   * For someone who has LEFT the company it is always '' — the team directory
   * (and the name key) may still hold their photo from before they left, so this
   * is the ONE place every screen asks, rather than each re-checking the flag.
   */
  function photoFor(subject: AvatarSubject): string {
    if (subject.separated) return '';
    return subject.image || get(subject.id, subject.name);
  }

  /**
   * {@link ensureUsers} for the people on screen, never queueing a profile lookup
   * for someone who has left the company (their profile 404s, and a hit would
   * only resurrect the photo the server withheld).
   */
  function ensureFor(subjects: readonly AvatarSubject[], limit?: number): void {
    ensureUsers(
      subjects.filter((s) => !s.separated).map((s) => s.id),
      limit,
    );
  }

  /** Drop everything on sign-out — the directory is tenant-scoped. */
  function reset(): void {
    byUserId.value = {};
    attempted.value = new Set();
    loaded.value = false;
    inFlight = null;
    queue.length = 0;
  }

  return { byUserId, loaded, get, photoFor, ensureLoaded, ensureUsers, ensureFor, reset };
});
